import { money } from "@/lib/fmt";
import { FIELDS, TARGET_FIELDS } from "@/lib/opsEstimate";

const tone = (dark) => ({
  inp: dark ? "bg-white/5 border-white/15 text-white" : "bg-white border-input",
  lab: dark ? "text-stone-300" : "text-stone-700",
  help: dark ? "text-stone-400" : "text-stone-500",
});

export function NumField({ id, def, value, onChange, error, dark, prefix }) {
  const c = tone(dark), fid = `${prefix}-${id}`;
  return (
    <div>
      <label htmlFor={fid} className={`text-sm font-medium ${c.lab}`}>{def.label}</label>
      <input id={fid} type="text" inputMode="decimal" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={`${fid}-msg`}
        className={`mt-1 w-full h-12 rounded-lg border px-3 text-base num focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9E2A2B]/50 ${c.inp} ${error ? "border-rose-500" : ""}`} data-testid={`${prefix}-${id}`} />
      <div id={`${fid}-msg`} className={`text-[11px] mt-1 ${error ? "text-rose-500 font-medium" : c.help}`} data-testid={`${prefix}-${id}-msg`}>{error || def.help || ""}</div>
    </div>
  );
}

export function FieldGrid({ keys, raw, setRaw, errors, dark, prefix, defs = FIELDS }) {
  return <div className="grid sm:grid-cols-2 gap-4">{keys.map((k) => <NumField key={k} id={k} def={defs[k]} value={raw[k]} onChange={(x) => setRaw({ ...raw, [k]: x })} error={errors[k]} dark={dark} prefix={prefix} />)}</div>;
}

export function TargetsEditor({ keys, raw, setRaw, errors, dark, prefix }) {
  return (
    <details className={`rounded-xl border p-4 ${dark ? "border-white/15" : "border-[#E6E1D7] bg-white"}`} data-testid={`${prefix}-targets`}>
      <summary className={`cursor-pointer text-sm font-semibold ${dark ? "text-white" : ""}`}>Your targets (prefilled with illustrative defaults — change them to your own)</summary>
      <div className="mt-3"><FieldGrid keys={keys} raw={raw} setRaw={setRaw} errors={errors} dark={dark} prefix={`${prefix}-t`} defs={TARGET_FIELDS} /></div>
    </details>
  );
}

const Line = ({ r, dim, testid }) => (
  <div className={`border-b border-stone-500/20 pb-2 ${dim ? "opacity-80" : ""}`} data-testid={testid}>
    <div className="flex justify-between gap-3"><span>{r.label}</span><span className="num">{r.value == null ? "Unknown — not entered" : money(r.value)}</span></div>
    <div className="text-[11px] opacity-70">{r.formula}</div>
  </div>
);

export function EstimateResult({ res, dark, prefix }) {
  return (
    <div className={`space-y-4 text-sm ${dark ? "text-stone-300" : "text-stone-700"}`} data-testid={`${prefix}-result`}>
      <div>
        <div className="label-eyebrow opacity-70">Estimated areas for review · per month</div>
        <div className={`font-display text-5xl mt-2 num ${dark ? "text-white" : "text-stone-900"}`} data-testid={`${prefix}-total`}>{res.known ? money(res.monthly) : "Not enough data"}</div>
        <div className="opacity-70 mt-1" data-testid={`${prefix}-annual`}>{res.known ? `≈ ${money(res.annual)} per year (monthly × 12)` : "Enter at least one of the percentages below to see an estimate."}</div>
        {!res.complete && res.known > 0 && <div className="mt-2 rounded-lg bg-amber-100 text-amber-900 p-2 text-xs" data-testid={`${prefix}-partial`}>Partial estimate — not entered: {res.unknown.join(", ")}. Unknown values are left out, not treated as zero.</div>}
      </div>
      <div className="space-y-2">{res.review.map((r) => <Line key={r.key} r={r} testid={`${prefix}-row-${r.key}`} />)}</div>
      {!!res.context.length && <div><div className="font-semibold mb-1">Shown for context — not counted above</div><div className="space-y-2">{res.context.map((r) => <Line key={r.key} r={r} dim testid={`${prefix}-row-${r.key}`} />)}</div></div>}
      {res.time && <div data-testid={`${prefix}-time`}><div className="font-semibold">Estimated manager time value (not cash savings)</div>
        <div className="flex justify-between"><span>Admin hours × 52 ÷ 12 × your % × hourly cost</span><span className="num">{res.time.hours == null ? "Unknown — not entered" : `${money(res.time.hours)}/mo`}</span></div></div>}
      <p className="text-[11px] opacity-70" data-testid={`${prefix}-disclaimer`}>An operational estimate, not guaranteed savings. Period: one month, using the net sales you entered as the denominator. Only amounts above your own targets are counted; comps are counted once when your POS reports them inside discounts; inventory variance is excluded because it can overlap with waste. Totals are rounded to the nearest $10. Using 86'd does not by itself capture these amounts — they are places to investigate.</p>
    </div>
  );
}
